import React, { useEffect, useRef, useState } from 'react';

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Returns a ref and a flag that flips once the element scrolls into view. */
export function useInView<T extends HTMLElement>(margin = '0px 0px -12% 0px') {
    const ref = useRef<T | null>(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (!('IntersectionObserver' in window)) {
            setInView(true);
            return;
        }
        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                    io.disconnect();
                }
            },
            { rootMargin: margin }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [margin]);

    return [ref, inView] as const;
}

/** Wrapper that adds `is-in` when visible; children animate via .fade / .mask / .rule classes. */
export function Reveal({ className = '', children }: { className?: string; children: React.ReactNode }) {
    const [ref, inView] = useInView<HTMLDivElement>();
    return (
        <div ref={ref} className={`reveal ${inView ? 'is-in' : ''} ${className}`}>
            {children}
        </div>
    );
}

/** Delay helper for CSS custom property. */
export const d = (ms: number) => ({ '--d': `${ms}ms` } as React.CSSProperties);

const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/·—';

/** Mono label that resolves from noise into its text, once, when it scrolls into view. */
export function Scramble({ text, delay = 0, className = '' }: { text: string; delay?: number; className?: string }) {
    const [ref, inView] = useInView<HTMLSpanElement>();
    const [out, setOut] = useState(text.replace(/\S/g, ' '));

    useEffect(() => {
        if (!inView) return;
        if (prefersReducedMotion()) {
            setOut(text);
            return;
        }
        let raf = 0;
        let start = 0;
        const duration = 700 + text.length * 18;
        const tick = (t: number) => {
            if (!start) start = t;
            const p = Math.min(1, (t - start) / duration);
            const settled = Math.floor(p * text.length);
            let s = '';
            for (let i = 0; i < text.length; i++) {
                const c = text[i];
                if (i < settled || c === ' ') s += c;
                else if (i < settled + 6) s += GLYPHS[(Math.random() * GLYPHS.length) | 0];
                else s += ' ';
            }
            setOut(s);
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        const timer = window.setTimeout(() => (raf = requestAnimationFrame(tick)), delay);
        return () => {
            window.clearTimeout(timer);
            cancelAnimationFrame(raf);
        };
    }, [inView, text, delay]);

    return (
        <span ref={ref} className={className} aria-label={text}>
            <span aria-hidden="true">{out}</span>
        </span>
    );
}

/** Scroll-linked translate for slow parallax, written straight to the DOM to avoid re-renders. */
export function useParallax<T extends HTMLElement | SVGElement>(factor = 0.15) {
    const ref = useRef<T | null>(null);

    useEffect(() => {
        if (prefersReducedMotion()) return;
        let raf = 0;
        const update = () => {
            raf = 0;
            if (ref.current) ref.current.style.transform = `translate3d(0, ${window.scrollY * factor}px, 0)`;
        };
        const onScroll = () => {
            if (!raf) raf = requestAnimationFrame(update);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            cancelAnimationFrame(raf);
        };
    }, [factor]);

    return ref;
}
